'use client'

import { useRouter, useSearchParams } from 'next/navigation'

import {
  getTenantLocale,
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../global-config'
import { tenantAndEnvAccessorIsValidLanguage } from '../tenant'
import {
  urlResolverGetAccount,
  urlResolverGetCart,
  urlResolverGetCheckoutAddress,
} from '../url-handling'

import {
  CART_ACTION,
  CHECKOUT_ADDRESS_TARGET_PAGE_CODE,
  LOGIN_SOURCE_PAGE,
  REGISTRATION_SOURCE_PAGE,
  TARGET_PAGE_CODE_PARAM,
  TARGET_PAGE_REDIRECT_PARAM,
} from './consts'

/**
 * Append `after=<sourcePage>` to a relative URL, source can be login or registration
 * and always return only the path+search. eg: /nx/account?after=login
 *
 * @param path            A site-relative URL (e.g. "/nx/account")
 * @param sourcePage      The value for the `after` query parameter.
 * @param shopOrigin  The origin to use as the base (e.g. "https://www.shop-apotheke.com/").
 */
const appendAfterParameter = (
  path: string,
  sourcePage: string,
  shopOrigin: string,
): string => {
  try {
    const url = new URL(path, shopOrigin)
    url.searchParams.append('after', sourcePage)

    return url.pathname + url.search
  } catch {
    return path
  }
}

/**
 * Safely converts any provided URL into a domain-relative path
 * (e.g., "/some/path?query=123#hash") by discarding the external host.
 *
 * If the URL is invalid or cannot be parsed, returns the fallback URL.
 */
const sanitizeToRelativeUrlOrFallback = (
  url: string,
  fallbackUrl: string,
  shopUrl: string,
): string => {
  try {
    const { hash, pathname, search } = new URL(url, shopUrl)

    return `${pathname}${search}${hash}`
  } catch {
    return fallbackUrl
  }
}

/**
 * Determines the final redirect URL based on target page codes,
 * source page, and optional loginRedirectUrl.
 */
const getDestinationUrl = ({
  globalConfig,
  loginRedirectUrl,
  sourcePage,
  targetPageCode,
}: {
  globalConfig: PublicGlobalConfig
  loginRedirectUrl: string
  sourcePage: string
  targetPageCode: string
}): string => {
  const { language, shopUrl, tenantAndEnv } = globalConfig

  if (
    targetPageCode === CHECKOUT_ADDRESS_TARGET_PAGE_CODE &&
    sourcePage === LOGIN_SOURCE_PAGE
  ) {
    return urlResolverGetCart(globalConfig, CART_ACTION.CHECK_OVERDRAFTS)
  }

  if (loginRedirectUrl) {
    const fallback = urlResolverGetAccount(globalConfig)
    const sanitizedRelativeUrl = sanitizeToRelativeUrlOrFallback(
      loginRedirectUrl,
      fallback,
      shopUrl,
    )

    return appendAfterParameter(sanitizedRelativeUrl, sourcePage, shopUrl)
  }

  const destinationUrl =
    targetPageCode === CHECKOUT_ADDRESS_TARGET_PAGE_CODE
      ? urlResolverGetCheckoutAddress(globalConfig, tenantAndEnv, language)
      : urlResolverGetAccount(globalConfig)

  return appendAfterParameter(destinationUrl, sourcePage, shopUrl)
}

/**
 * Updates the globalConfig with the preferred language and locale if valid.
 * @param globalConfig The current global configuration object.
 * @param preferredLanguage The preferred language to update.
 * @returns Updated globalConfig object.
 */
const updateGlobalConfigWithPreferredLanguage = (
  globalConfig: PublicGlobalConfig,
  preferredLanguage: string | undefined,
): PublicGlobalConfig => {
  if (
    preferredLanguage &&
    tenantAndEnvAccessorIsValidLanguage(preferredLanguage)
  ) {
    const locale = getTenantLocale(globalConfig.tenant, preferredLanguage)

    return {
      ...globalConfig,
      language: preferredLanguage,
      locale,
    }
  }

  return globalConfig
}

/**
 * After login, fetches the user’s preferred language (if available)
 * and returns an updated global config with that language applied.
 *
 * If fetching the preferred language fails (e.g., network error),
 * the original `globalConfig` is returned unchanged.
 *
 * @param globalConfig
 *   The existing application-wide public configuration.
 * @param afterLoginPromiseWithPreferredLanguage
 *   A promise resolving to the user’s preferred language (or undefined).
 * @returns A promise resolving to the updated global config.
 */
export const updateGlobalConfigWithPreferredLanguageAfterLogin = async (
  globalConfig: PublicGlobalConfig,
  afterLoginPromiseWithPreferredLanguage: Promise<string | undefined>,
): Promise<PublicGlobalConfig> => {
  const preferredLanguage = await afterLoginPromiseWithPreferredLanguage.catch(
    () => undefined,
  )

  return updateGlobalConfigWithPreferredLanguage(
    globalConfig,
    preferredLanguage,
  )
}

/**
 * Returns a function you can call after login to redirect immediately,
 * without touching language.
 */
export const useGetRedirectToTargetPageAfterRegistration = () => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const globalConfig = useGlobalConfigContext()

  const loginRedirectUrl = searchParams.get('redirect') ?? ''
  const targetPageCode = searchParams.get('code') ?? ''

  return (): void => {
    const url = getDestinationUrl({
      globalConfig,
      loginRedirectUrl,
      sourcePage: REGISTRATION_SOURCE_PAGE,
      targetPageCode,
    })
    router.push(url)
  }
}

/**
 * This custom hook redirects the user to a target page based on the URL search parameters and the source page
 * eg: for login from checkout
 *  - URL /nx/login?targetPageCode=checkoutAddress
 *  - const redirectToTargetPage = useGetRedirectToTargetPage('login')
 *
 */
export const useGetRedirectToTargetPageAfterLogin = (): ((
  afterLoginPromiseWithPreferredLanguage: Promise<string | undefined>,
) => void) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const globalConfig = useGlobalConfigContext()

  const loginRedirectUrl = searchParams.get(TARGET_PAGE_REDIRECT_PARAM) ?? ''
  const targetPageCode = searchParams.get(TARGET_PAGE_CODE_PARAM) ?? ''

  const redirectAfterLoginWithPreferredLanguage = async (
    afterLoginPromiseWithPreferredLanguage: Promise<string | undefined>,
  ): Promise<void> => {
    const globalConfigWithPreferredLanguage =
      await updateGlobalConfigWithPreferredLanguageAfterLogin(
        globalConfig,
        afterLoginPromiseWithPreferredLanguage,
      )
    const redirectUrl = getDestinationUrl({
      globalConfig: globalConfigWithPreferredLanguage,
      loginRedirectUrl,
      sourcePage: LOGIN_SOURCE_PAGE,
      targetPageCode,
    })
    router.push(redirectUrl)
  }

  // Return a function that performs the actual navigation
  return (
    afterLoginPromiseWithPreferredLanguage: Promise<string | undefined>,
  ): void => {
    void redirectAfterLoginWithPreferredLanguage(
      afterLoginPromiseWithPreferredLanguage,
    )
  }
}
